import React from 'react'
import { Typography } from 'antd'
import Table from '../../../components/lists/Table/Table'
import ColumnSelector from './ColumnSelector'
import ExcelExporter from '../../../components/ExcelExporter'
import {
  createTeaserColumns,
  channelColumns,
  getTeaserColumnOptions
} from './columnConfigs'
import {
  calculateTeaserTotals,
  calculateChannelTotals,
  createTeaserSummaryExportRow,
  createChannelSummaryExportRow
} from './utils'
import { roundTo } from './calculations'
import { TeaserStatus, CompanyType } from '../../../__generated__/graphql'
import { useOutletContext } from 'react-router-dom'
import { fetchTeaserDateBreakdown } from './dateBreakdownExport'
import { fetchGenericDateBreakdown, CHANNEL_COLUMN_CONFIG } from './dateBreakdownExportGeneric'

const { Title, Text } = Typography

interface PerformanceOverviewProps {
  visibleColumns: string[]
  onVisibleColumnsChange: (columns: string[]) => void
  onCreateOrUpdate: (row?: any) => void
  onUpdateStatus: (row: any, status: TeaserStatus) => void
  selectedMetric: string
  companyType: string
  teaserData?: any[]
  channelData?: any[]
  channelBreakdownType?: 'website' | 'source'
  onChannelBreakdownTypeChange?: (type: 'website' | 'source') => void
  cubejsApi?: any
  campaignId?: string
  formattedDateRange?: string[]
  aggPublisherFilters?: any[]
  teasers?: any[]
}

const PerformanceOverview: React.FC<PerformanceOverviewProps> = ({
  visibleColumns,
  onVisibleColumnsChange,
  onCreateOrUpdate,
  onUpdateStatus,
  companyType,
  teaserData: realTeaserData,
  channelData: realChannelData,
  channelBreakdownType = 'website',
  onChannelBreakdownTypeChange,
  cubejsApi,
  campaignId,
  formattedDateRange,
  aggPublisherFilters = [],
  teasers = []
}) => {
  const { campaign } = useOutletContext() as { campaign: any }

  // Use real data only - no fallback to mock data
  const finalTeaserData = realTeaserData || []
  const finalChannelData = realChannelData || []

  const teaserTotals = React.useMemo(
    () => calculateTeaserTotals(finalTeaserData),
    [finalTeaserData]
  )

  const channelsWithoutTeasers = React.useMemo(
    () => finalChannelData.filter(channel => channel.channel !== 'Teasers'),
    [finalChannelData]
  )

  const channelTotals = React.useMemo(
    () => calculateChannelTotals(channelsWithoutTeasers),
    [channelsWithoutTeasers]
  )

  const teaserSummaryRowForExport = React.useMemo(() => {
    if (!finalTeaserData.length) {
      return undefined
    }

    return createTeaserSummaryExportRow(teaserTotals, companyType)
  }, [companyType, finalTeaserData.length, teaserTotals])

  const channelSummaryRowForExport = React.useMemo(() => {
    if (!channelsWithoutTeasers.length) {
      return undefined
    }

    return createChannelSummaryExportRow(channelTotals)
  }, [channelTotals, channelsWithoutTeasers.length])

  // Handler to fetch teaser date breakdown data before export
  const handleBeforeExport = async () => {
    if (!cubejsApi || !campaignId || !formattedDateRange) {
      console.warn('Missing required props for date breakdown export')
      return []
    }

    try {
      const dateBreakdownData = await fetchTeaserDateBreakdown(
        cubejsApi,
        campaignId,
        formattedDateRange,
        aggPublisherFilters,
        teasers,
        companyType,
        visibleColumns
      )

      return [
        {
          name: 'Date Breakdown',
          data: dateBreakdownData,
        }
      ]
    } catch (error) {
      console.error('Error fetching date breakdown data:', error)
      return []
    }
  }

  // Handler to fetch channel date breakdown data before export
  const handleChannelBeforeExport = async () => {
    if (!cubejsApi || !campaignId || !formattedDateRange) {
      console.warn('Missing required props for channel date breakdown export')
      return []
    }

    try {
      // Get visible columns from channel table (all channels show same columns)
      const channelVisibleColumns = channelColumns(channelBreakdownType).map(col => col.key)

      const dateBreakdownData = await fetchGenericDateBreakdown(
        cubejsApi,
        campaignId,
        formattedDateRange,
        aggPublisherFilters,
        channelVisibleColumns,
        companyType,
        CHANNEL_COLUMN_CONFIG,
        'channel',
        (utmSource) => utmSource !== '' && utmSource !== 'ADSERVER' // Exclude teasers
      )

      return [
        {
          name: 'Date Breakdown',
          data: dateBreakdownData,
        }
      ]
    } catch (error) {
      console.error('Error fetching channel date breakdown data:', error)
      return []
    }
  }

  // Create columns with action handlers
  const allTeaserColumns = createTeaserColumns(onCreateOrUpdate, onUpdateStatus)
  const teaserColumns = allTeaserColumns.filter(({ key, dataIndex }) =>
    visibleColumns.includes(dataIndex || '') || key === 'actions'
  )

  // Create column mapping for Excel export (human-readable titles)
  const teaserColumnMapping = allTeaserColumns
    .filter(col => col.dataIndex && visibleColumns.includes(col.dataIndex))
    .filter(col => typeof col.title === 'string') // Only include string titles
    .map(col => ({
      title: col.title as string,
      dataIndex: col.dataIndex!
    }))

  // Create channel column mapping
  const allChannelColumns = channelColumns(channelBreakdownType)
  const channelColumnMapping = allChannelColumns.map(col => ({
    title: col.title,
    dataIndex: col.dataIndex
  }))

  // Define fields to exclude based on company type
  const getExcludeFields = () => {
    const baseExcludeFields = [
      'tdImage', // Don't export images
      'tdType' // Internal teaser type
    ]

    if (companyType === CompanyType.Agency || companyType === CompanyType.Advertiser) {
      baseExcludeFields.push('publisherRevenue')
    }

    if (companyType === CompanyType.Publisher) {
      baseExcludeFields.push('totalCost')
    }

    return baseExcludeFields
  }

  const getTeaserSummaryData = () => {
    const totals = teaserTotals
    const averageReadtime = totals.reads > 0 ? roundTo(totals.totalReadtime / (totals.reads*1000), 1) : 0
    const publisherRevenue = roundTo(totals.publisherRevenue, 2)
    const totalCost = roundTo(totals.totalCost, 2)
    const impressionsInThousands = totals.tdImpressions / 1000
    const eCpmValue = impressionsInThousands > 0
      ? (companyType === CompanyType.Publisher
        ? roundTo(publisherRevenue / impressionsInThousands, 2)
        : roundTo(totalCost / impressionsInThousands, 2))
      : 0
    const cpaValue = totals.conversions > 0 ? roundTo(totalCost / totals.conversions, 2) : 0
    const ctrValue = totals.tdImpressions > 0 ? roundTo((totals.tdClicks / totals.tdImpressions) * 100, 2) : 0
    const impsReadsValue = totals.tdImpressions > 0 ? roundTo((totals.reads / totals.tdImpressions) * 1000, 2) : 0
    const cotrValue = totals.reads > 0 ? roundTo((totals.clickOuts / totals.reads) * 100, 2) : 0

    return {
      name: 'Total',
      tdImpressions: totals.tdImpressions.toLocaleString(),
      tdClicks: totals.tdClicks.toLocaleString(),
      reads: totals.reads.toLocaleString(),
      avgReadtime: `${averageReadtime.toFixed(1)}sec`,
      publisherRevenue: (
        <span>
          <span className="text-xs text-gray-500">DKK </span>
          {publisherRevenue.toFixed(2)}
        </span>
      ),
      totalCost: (
        <span>
          <span className="text-xs text-gray-500">DKK </span>
          {totalCost.toFixed(2)}
        </span>
      ),
      clickOuts: totals.clickOuts.toLocaleString(),
      conversions: totals.conversions,
      ctr: ctrValue + '%',
      eCpm: totals.tdImpressions > 0 ? (
        <span>
          <span className="text-xs text-gray-500">DKK </span>
          {eCpmValue.toFixed(2)}
        </span>
      ) : '-',
      cpa: totals.conversions > 0 ? (
        <span>
          <span className="text-xs text-gray-500">DKK </span>
          {cpaValue.toFixed(2)}
        </span>
      ) : '-',
      imps_reads: impsReadsValue + '‰',
      cotr: cotrValue + '%'
    }
  }

  const getChannelSummaryData = () => {
    const totals = channelTotals
    const averageReadtime = totals.reads > 0 ? roundTo(totals.totalReadtime / (totals.reads*1000), 1) : 0

    return {
      channel: 'Total',
      reads: totals.reads.toLocaleString(),
      avgReadtime: totals.reads > 0 ? averageReadtime.toFixed(1) + 'sec' : '-',
      clickOuts: totals.clickOuts.toLocaleString(),
      conversions: totals.conversions.toLocaleString()
    }
  }

  return (
    <div className="space-y-6">
      {/* Teasers Table */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <div>
            <Title level={4} className="!mb-1">Teasers Performance</Title>
            <Text type="secondary">Campaign teaser performance metrics</Text>
          </div>
          <ColumnSelector
            options={getTeaserColumnOptions(companyType)}
            value={visibleColumns}
            onChange={onVisibleColumnsChange}
          />
        </div>
        <Table
          columns={teaserColumns}
          data={finalTeaserData}
          searchable={false}
          showSummary
          summaryData={getTeaserSummaryData()}
          footer={() => (
            <ExcelExporter
              data={finalTeaserData}
              visibleColumns={visibleColumns}
              columnMapping={teaserColumnMapping}
              excludeFields={getExcludeFields()}
              summaryRow={teaserSummaryRowForExport}
              filename="TeasersData"
              buttonText="Export to Excel"
              trackingContext={{
                campaignId: campaign?._id,
                exportType: 'teasers_performance',
                companyType,
              }}
              onBeforeExport={handleBeforeExport}
              exportingText="Preparing export with date breakdown..."
            />
          )}
        />
      </div>

      {/* Other Channels Table */}
      <div>
        <div className="mb-3">
          <div className="flex justify-between items-start">
            <div>
              <Title level={4} className="!mb-1">Other Channels Performance</Title>
              <Text type="secondary">Traffic from social, organic, and direct channels</Text>
            </div>
            <div className="inline-flex rounded-md shadow-sm" role="group">
              <button
                type="button"
                onClick={() => {
                  onChannelBreakdownTypeChange?.('website')
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-l-md border ${
                  channelBreakdownType === 'website'
                    ? 'bg-indigo-600 text-white border-indigo-600 z-10'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Website
              </button>
              <button
                type="button"
                onClick={() => {
                  onChannelBreakdownTypeChange?.('source')
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-r-md border-l-0 border ${
                  channelBreakdownType === 'source'
                    ? 'bg-indigo-600 text-white border-indigo-600 z-10'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Source
              </button>
            </div>
          </div>
        </div>
        <Table
          columns={channelColumns(channelBreakdownType)}
          data={channelsWithoutTeasers}
          searchable={false}
          showSummary
          summaryData={getChannelSummaryData()}
          expandable={{
            defaultExpandAllRows: false,
            expandRowByClick: true
          }}
          footer={() => (
            <ExcelExporter
              data={channelsWithoutTeasers}
              columnMapping={channelColumnMapping}
              summaryRow={channelSummaryRowForExport}
              filename="ChannelsData"
              trackingContext={{
                campaignId: campaign?._id,
                exportType: 'channels_performance',
                channelBreakdownType,
              }}
              buttonText="Export to Excel"
              onBeforeExport={handleChannelBeforeExport}
              exportingText="Preparing export with date breakdown..."
            />
          )}
        />
      </div>
    </div>
  )
}

export default PerformanceOverview


